import { useMemo, useState } from 'react';
import {
  CircleCheckIcon,
  CircleDashedIcon,
  CirclePauseIcon,
  PlusIcon,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useWorkspaceList } from '@/features/workspaces';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import { RetryButton } from '@/shared/components/RetryButton';

import { workspaceImpact } from '../commitCoverage';
import {
  useForgePolicies,
  usePeopleDirectory,
  useSetForgePolicy,
} from '../hooks/useLinkedAccounts';
import {
  LINKED_ACCOUNTS_COPY,
  POLICY_RAIL_COPY as WORDS,
} from '../linkedAccountsCopy';
import { RequirePolicySheet } from './RequirePolicySheet';

const ALL_WORKSPACES = 200;

/**
 * Required in (spec 165 US1): the workspaces that require each person's own
 * account, each with what it costs, as people who cannot start runs there.
 * Requiring one more goes through a sheet that shows its cost first;
 * stopping stops nobody, so it saves at once.
 */
export function RequiredInRail() {
  const workspaces = useWorkspaceList({ limit: ALL_WORKSPACES });
  const policies = useForgePolicies();
  const directory = usePeopleDirectory();
  const setPolicy = useSetForgePolicy();
  const [choosing, setChoosing] = useState(false);

  const names = useMemo(
    () =>
      new Map(
        (workspaces.data?.items ?? []).map((workspace) => [
          workspace.id,
          workspace.name,
        ]),
      ),
    [workspaces.data?.items],
  );
  const required = useMemo(
    () =>
      (policies.data?.items ?? [])
        .map((policy) => ({
          workspaceId: policy.workspace_id,
          name: names.get(policy.workspace_id) ?? null,
        }))
        .filter(
          (row): row is { workspaceId: string; name: string } =>
            row.name !== null,
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [policies.data?.items, names],
  );
  const failed = workspaces.isError || policies.isError;
  const loaded = workspaces.data && policies.data;

  return (
    <section
      aria-labelledby="commit-identity-required-in"
      className="border-border bg-card flex flex-col gap-3 rounded-md border p-4"
    >
      <div className="flex flex-col gap-1">
        <h2 id="commit-identity-required-in">
          <EyebrowLabel>{WORDS.requiredIn}</EyebrowLabel>
        </h2>
        <p className="text-muted-foreground text-xs">{WORDS.requiredIntro}</p>
      </div>

      {failed ? (
        <div className="flex items-center gap-3">
          <p className="text-muted-foreground text-sm">
            {LINKED_ACCOUNTS_COPY.policiesFailed}
          </p>
          <RetryButton
            onRetry={() => {
              void workspaces.refetch();
              void policies.refetch();
            }}
            isFetching={workspaces.isFetching || policies.isFetching}
          />
        </div>
      ) : !loaded ? null : required.length === 0 ? (
        <p className="text-muted-foreground text-sm">{WORDS.noneRequired}</p>
      ) : (
        <ul className="divide-border flex flex-col divide-y">
          {required.map((row) => {
            const impact = directory.data
              ? workspaceImpact(
                  row.workspaceId,
                  directory.data.items,
                  directory.data.hosts,
                )
              : null;
            const stopped = impact?.stopped.length ?? 0;
            // A workspace with no forge stops nobody, but nothing is fine
            // there either: a neutral mark, not a check (spec 169).
            const Icon =
              impact && impact.hosts.length === 0
                ? CircleDashedIcon
                : stopped > 0
                  ? CirclePauseIcon
                  : CircleCheckIcon;
            return (
              <li
                key={row.workspaceId}
                className="flex items-start justify-between gap-2 py-2 first:pt-0 last:pb-0"
              >
                <div className="flex min-w-0 flex-col gap-0.5">
                  {/* Two lines, then the whole name on hover: names here run
                      long, and a cut name could be the wrong workspace. */}
                  <span
                    title={row.name}
                    className="text-foreground line-clamp-2 text-sm font-medium [overflow-wrap:anywhere]"
                  >
                    {row.name}
                  </span>
                  {impact ? (
                    <span
                      className={`flex items-center gap-1 text-xs ${
                        stopped > 0
                          ? 'text-state-danger'
                          : 'text-muted-foreground'
                      }`}
                    >
                      <Icon aria-hidden="true" className="size-3.5 shrink-0" />
                      {impact.hosts.length === 0
                        ? WORDS.noForge
                        : WORDS.stops(stopped)}
                    </span>
                  ) : null}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={WORDS.stopRequiringLabel(row.name)}
                  disabled={setPolicy.isPending}
                  onClick={() =>
                    setPolicy.mutate({
                      workspaceId: row.workspaceId,
                      name: row.name,
                      required: false,
                    })
                  }
                >
                  {WORDS.stopRequiring}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      {loaded ? (
        <Button
          variant="outline"
          size="sm"
          className="self-start"
          onClick={() => setChoosing(true)}
        >
          <PlusIcon aria-hidden="true" />
          {WORDS.requireIn}
        </Button>
      ) : null}

      <RequirePolicySheet
        open={choosing}
        onClose={() => setChoosing(false)}
        workspaces={workspaces.data?.items ?? []}
        requiredIds={required.map((row) => row.workspaceId)}
      />
    </section>
  );
}
